import { Entries } from 'aws-sdk/clients/iottwinmaker'
import React from 'react'
import styled, { CSSProperties } from 'styled-components'

const swatchSize = 12
const swatchSpace = 1

type typeType =
  | 'line'
  | 'rect'
  | 'none'
  | 'rect-pattern'
  | 'rect-multi'
  | 'circle'
  | 'cross'
  | 'icon'

type EntryType = {
  type: typeType
  label: string | JSX.Element
  color: string | string[]
  icon?: string
  size?: number
}
type EntryGroupType = {
  type?: typeType
  label?: string
  color?: string | string[]
  icon?: string
  size?: number
  entries: EntryType[]
}
export const Key = ({
  swatchSize = 12,
  entries,
  style,
}: {
  swatchSize?: number
  entries?: EntryGroupType[]
  style?: CSSProperties
}) => {
  return (
    <StyledKey style={style}>
      {entries.map((entrygroup: EntryGroupType,i:number) => {
        const {
          label,
          entries,
          type,
          color = '#202346',
          icon,
          size,
        } = entrygroup
        return (
          <Group key={"keygroup_"+i}>
            {label && (
              <GroupTitle>
                {type && (
                  <Swatches
                    type={type}
                    color={color}
                    icon={icon}
                    size={size || swatchSize}
                  />
                )}{' '}
                {label}
              </GroupTitle>
            )}
            <RenderEntries entries={entries} />
          </Group>
        )
      })}
    </StyledKey>
  )
}

const Swatches = ({
  type,
  color,
  icon,
  size,
}: {
  type: typeType
  color?: string | string[]
  icon?: string
  size: number
}) => {
  return (
    <>
      {type === 'rect' ? (
        <SwatchShape color={color} swatchSize={size} />
      ) : type === 'rect-pattern' ? (
        <SwatchShapePattern color={color as string} swatchSize={size} />
      ) : type === 'line' ? (
        <SwatchLine color={color as string} swatchSize={size} />
      ) : type === 'rect-multi' ? (
        <SwatchShapeMulti color={[color as string]} swatchSize={size} />
      ) : type === 'circle' ? (
        <SwatchCircle color={color as string} swatchSize={size} />
      ) : type === 'cross' ? (
        <SwatchCross color={color as string} swatchSize={size} />
      ) : type === 'icon' && icon ? (
        <SwatchIcon icon={icon} swatchSize={size} />
      ) : (
        <SwatchNone swatchSize={size} />
      )}
    </>
  )
}
const RenderEntries = ({ entries }: { entries: EntryType[] }) => {
  return (
    <>
      {entries &&
        entries.map((m, i) => {
          return (
            <Entry key={'Key_' + i}>
              <div className='swatch'>
                <Swatches
                  type={m.type}
                  color={m.color}
                  size={m.size || swatchSize}
                />
              </div>
              <div className='label'>{m.label}</div>
            </Entry>
          )
        })}
    </>
  )
}

const Group = styled.div`
  display: flex;
  width: 100%;
  font-size: 15px;
  flex-wrap: wrap;
`
const GroupTitle = styled.div`
  flex: 0 0 100%;
  font-weight: 700;
  display: flex;
  align-items: flex-start;
  margin-top: 0.25em;
  color: var(--text-color);
  .label{
    padding-top: 30px;

  }
`
const StyledKey = styled.div`
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 0em;
  margin-bottom: 0.5em;
  flex: 0 0 15%;
  width: 100%;
  @media only screen and (max-width: 711px) {
    /* flex: 0 0 100%; */
    padding-left: 2rem;
    padding-right: 2rem;
    width: calc(100% - 4rem);
  }
`

const Entry = styled.div`
  font-size: 15px;
  display: flex;
  align-items: center;
  flex: 1 1 25%;
  margin-top: .2em;
  line-height: 1.4em;
  color: var(--text-color);
  .label {
    flex: 1 1 100%;
    color: var(--text-color);
    span {
      white-space: nowrap;
    }
  }

  @media only screen and (max-width: 711px) {
    font-size: 13px;
    flex: 0 0 100%;
  }
`

const SwatchShapeMulti = ({
  color,
  swatchSize,
}: {
  color: string[]
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize * 2 + swatchSpace} height={swatchSize}>
      <rect width={swatchSize} height={swatchSize} fill={color[0]} />
      <rect
        x={swatchSize + swatchSpace}
        width={swatchSize}
        height={swatchSize}
        fill={color[1]}
      />
    </SVG>
  )
}
const IconHolder = styled.div<{ swatchSize }>`
  width: ${({ swatchSize }) => swatchSize + 'px'};
  margin-right: 0.5em;
  margin-left: ${({ swatchSize }) => -swatchSize / 2 + 'px'};
  img {
    max-width: 100%;
  }
`
const SwatchIcon = ({
  swatchSize,
  icon,
}: {
  swatchSize?: number
  icon: string
}) => {
  return <IconHolder swatchSize={swatchSize}>{<img src={icon} />}</IconHolder>
}
const SwatchShape = ({
  color,
  swatchSize,
}: {
  color: string | string[]
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize} height={swatchSize}>
      <rect
        width={swatchSize}
        height={swatchSize}
        fill={typeof color === 'object' ? color[0] : color}
        stroke={typeof color === 'object' && color[1] ? color[1] : 'none'}
      />
    </SVG>
  )
}
const SwatchCircle = ({
  color,
  swatchSize,
}: {
  color: string
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize + 2} height={swatchSize * 1.55}>
      <circle
        cx={swatchSize / 2 + 1}
        cy={swatchSize}
        r={swatchSize / 2}
        fill={typeof color === 'object' ? color[0] : color}
        stroke={typeof color === 'object' && color[1] ? color[1] : 'none'}
      />
    </SVG>
  )
}
const SwatchShapePattern = ({
  color,
  swatchSize,
}: {
  color: string
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize} height={swatchSize}>
      <pattern
        id='diagonalHatch'
        patternUnits='userSpaceOnUse'
        width='4'
        height='4'
        style={{ transform: 'scale(1.5)' }}
      >
        <path
          d='M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2'
          style={{ stroke: color, strokeWidth: 1 }}
        />
      </pattern>
      <rect width={swatchSize} height={swatchSize} fill='url(#diagonalHatch)' />
    </SVG>
  )
}
const SwatchLine = ({
  color,
  swatchSize,
}: {
  color: string
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize} height={swatchSize}>
      <line
        x2={swatchSize}
        y1={swatchSize}
        y2={0}
        stroke={color}
        strokeWidth={4}
      />
    </SVG>
  )
}
const SwatchCross = ({
  color,
  swatchSize,
}: {
  color: string
  swatchSize?: number
}) => {
  return (
    <SVG width={swatchSize} height={swatchSize}>
      <line
        x2={swatchSize}
        y1={swatchSize}
        y2={0}
        stroke={color}
        strokeWidth={3}
      />
      <line
        x1={swatchSize}
        y1={swatchSize}
        y2={0}
        stroke={color}
        strokeWidth={3}
      />
    </SVG>
  )
}
const SwatchNone = ({ swatchSize }: { swatchSize?: number }) => {
  return <svg width={swatchSize} height={swatchSize}></svg>
}
const SVG = styled.svg<{ width: number }>`
  margin-right: 5px;
  margin-left: ${({ width }) => -width / 2 + 'px'};
`
